import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PartialLabelData, WhatsAppTemplatesContent } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';

type WhatsAppTemplatePreviewProps = {
  templateType: 'confirmation' | 'reminder';
  language: 'en' | 'he';
  templates?: WhatsAppTemplatesContent;
  extraLines: Array<PartialLabelData>;
};

export const WhatsAppTemplatePreview = ({
  templateType,
  language,
  templates,
  extraLines,
}: WhatsAppTemplatePreviewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentLanguage = useLanguage();
  const shouldRenderRTL = language !== 'he' && currentLanguage === 'he';
  const { account } = useGetAccountAuthenticated({});
  const firstOrderPage = account?.experiences[0]?.order_pages?.[0];
  // Dummy data for preview
  const dummyData = useMemo(() => {
    if (language === 'he') {
      return {
        customerName: 'נטע ויזול',
        orderNumber: '123123',
        activityName: firstOrderPage?.label[language] || 'ניפוח זכוכית',
        dateTime: '2024.10.12 11:00',
        location: account.locations[0]?.address?.[language] || 'המיקום שלנו',
        extraLine1: extraLines[0]?.[language] || '',
        extraLine2: extraLines[1]?.[language] || '',
      };
    }
    return {
      customerName: 'John Doe',
      orderNumber: '123123',
      activityName: 'Glass Blowing',
      dateTime: '2024.10.12 11:00',
      location: account.locations[0]?.address?.[language] || 'Our location',
      extraLine1: extraLines[0]?.[language] || '',
      extraLine2: extraLines[1]?.[language] || '',
    };
  }, [language, extraLines]);

  const processTemplate = (template: string, data: typeof dummyData): string => {
    return template
      .replace('{0}', data.customerName)
      .replace('{1}', data.orderNumber)
      .replace('{2}', data.activityName)
      .replace('{3}', data.dateTime)
      .replace('{4}', data.location)
      .replace('{5}', data.extraLine1)
      .replace('{6}', data.extraLine2);
  };

  const getTemplateContent = (): string => {
    // Get base template from templates prop
    const baseTemplate = templates?.templates?.[`${templateType}_template`]?.[language];

    if (baseTemplate) {
      return processTemplate(baseTemplate, dummyData);
    }

    // Fallback to default template structure
    const defaultTemplate =
      templateType === 'confirmation'
        ? 'Hey {0}!\nYour booking {1} for {2} on {3} is confirmed.\nSee you at {4}.\n{5}\n{6}\nThe full booking details and invoice have been sent to your email.\nHave a great day from www.monkeybook.io.'
        : "Hello {0}!\nWe'd like to remind you about your booking {1}.\nThis is a reminder for {2} on {3} at {4}.\n{5}\n{6}\nHave a great day from www.monkeybook.io.";

    return processTemplate(defaultTemplate, dummyData);
  };

  const previewContent = getTemplateContent();

  const getMessageTitle = (): string => {
    const accountLabel = account?.label?.[language] || account?.label?.en || 'Business Name';

    if (templateType === 'confirmation') {
      return language === 'he' ? `אישור הזמנה עבור ${accountLabel}` : `Order Confirmation for ${accountLabel}`;
    }
    return language === 'he' ? `תזכורת עבור ${accountLabel}` : `Reminder for ${accountLabel}`;
  };

  return (
    <Card
      sx={{
        maxWidth: isSmallScreen ? '100%' : 400,
        backgroundImage: 'url(https://i.pinimg.com/564x/45/87/db/4587dba198ee103cf89c1f9c8828a1f4.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'center',
        borderRadius: '8px',
        minHeight: 200,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '8px',
          zIndex: 1,
        },
      }}>
      <CardContent sx={{ pb: 2, position: 'relative', zIndex: 2 }}>
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            display: 'block',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#333',
            fontSize: '12px',
            fontWeight: 600,
          }}>
          {t('whatsappSettings.preview.label')} ({language === 'he' ? 'עברית' : 'English'})
        </Typography>

        <Box
          sx={{
            backgroundColor: '#dcf8c6', // WhatsApp message green
            borderRadius: '8px',
            p: 2,
            position: 'relative',
            direction: shouldRenderRTL ? 'rtl' : 'ltr',
            textAlign: shouldRenderRTL ? 'right' : 'left',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '85%',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              [shouldRenderRTL ? 'right' : 'left']: -8,
              width: 0,
              height: 0,
              borderTop: '8px solid #dcf8c6',
              borderLeft: shouldRenderRTL ? 'none' : '8px solid transparent',
              borderRight: shouldRenderRTL ? '8px solid transparent' : 'none',
            },
          }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              color: '#000',
              mb: 1,
            }}>
            {getMessageTitle()}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: 1.4,
              whiteSpace: 'pre-line',
              color: '#000',
              fontSize: '15px',
              fontWeight: 400,
            }}>
            {previewContent}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: shouldRenderRTL ? 'right' : 'left',
              mt: 1,
              color: '#666',
              fontSize: '11px',
            }}>
            {new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
